import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import GamergyBracketManager from "../tournament-ops/GamergyBracketManager";

const GAME_BRANDING = {
  "rocket-league": {
    title: "Rocket League",
    heroTitle: "Operación · Rocket League",
    description: "Aquí corre solo la operación de Rocket League: registros, check-in, cupos, equipos y brackets de este título.",
    logoSrc: "/tournament-ops-assets/rocket-league-logo-desktop.png",
    logoAlt: "Rocket League",
    logoClassName: "max-h-12 sm:max-h-14 md:max-h-16 w-auto max-w-[180px] object-contain",
    decorSrc: "/tournament-ops-assets/rocket-league-decor.png",
    decorAlt: "Decoración Rocket League",
    decorClassName: "max-h-16 sm:max-h-20 md:max-h-24 w-auto max-w-[120px] object-contain opacity-95 drop-shadow-[0_18px_30px_rgba(15,23,42,0.18)]",
  },
  "pokemon-champions": {
    title: "Pokémon Champions",
    heroTitle: "Operación · Pokémon Champions",
    description: "Aquí corre solo la operación de Pokémon Champions: registros, check-in, cupos y brackets de este título.",
    logoSrc: "/gamergy-assets/pokemon-champions-logo.png",
    logoAlt: "Pokemon Champions",
    logoClassName: "max-h-14 md:max-h-16 w-auto object-contain",
    decorSrc: "/tournament-ops-assets/pokemon-decor.png",
    decorAlt: "Decoración Pokémon Champions",
    decorClassName: "max-h-24 md:max-h-32 w-auto object-contain opacity-95 drop-shadow-[0_18px_30px_rgba(15,23,42,0.18)]",
  },
  "cod-bo7": {
    title: "Call of Duty Black Ops 7",
    heroTitle: "Operación · COD BO7",
    description: "Aquí corre solo la operación de COD BO7: equipos, jugadores, check-in, cupos y brackets de este título.",
    logoSrc: "/gamergy-assets/blackops7-logo.png",
    logoAlt: "Call of Duty Black Ops 7",
    logoClassName: "max-h-16 md:max-h-20 w-auto object-contain",
    decorSrc: "/tournament-ops-assets/cod-decor.png",
    decorAlt: "Decoración COD BO7",
    decorClassName: "max-h-24 md:max-h-32 w-auto object-contain opacity-95 drop-shadow-[0_18px_30px_rgba(15,23,42,0.20)]",
  },
  fc26: {
    title: "FC 26",
    heroTitle: "Operación · FC 26",
    description: "Aquí corre solo la operación de FC 26: registros, check-in, cupos y brackets de este título.",
    logoSrc: "/gamergy-assets/fc26-logo.png",
    logoAlt: "FC 26",
    logoClassName: "max-h-14 md:max-h-16 w-auto object-contain",
    decorSrc: "/tournament-ops-assets/fc26-decor.png",
    decorAlt: "Decoración FC 26",
    decorClassName: "max-h-24 md:max-h-32 w-auto object-contain opacity-95 drop-shadow-[0_18px_30px_rgba(15,23,42,0.18)]",
  },
};

const PREL_ADMIN_SHELL_CLASS = "min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(0,92,245,0.14),transparent_34%),radial-gradient(circle_at_top_right,rgba(239,68,68,0.16),transparent_28%),linear-gradient(180deg,#f7f3ee_0%,#eef4ff_45%,#f8fbff_100%)] text-slate-950 px-4 py-5 sm:px-5 md:p-10";
const PREL_ADMIN_PANEL_CLASS = "rounded-2xl border border-slate-200/80 bg-white/82 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur";
const PREL_ADMIN_SOFT_PANEL_CLASS = "rounded-2xl border border-slate-200/70 bg-[#f8fbff]/92";
const PREL_ADMIN_INPUT_CLASS = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm placeholder:text-slate-400";
const PREL_ADMIN_SUBTLE_TEXT_CLASS = "text-slate-600";
const PREL_ADMIN_BADGE_CLASS = "text-xs uppercase tracking-[0.3em] text-[#0f4cc9]";

const PREL_OPS_SHELL_CLASS = "min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(124,58,237,0.22),transparent_24%),radial-gradient(circle_at_top_right,rgba(239,68,68,0.16),transparent_22%),linear-gradient(180deg,#fcfbff_0%,#f7f4fb_52%,#f6f1f4_100%)] px-4 py-5 text-[#111111] sm:px-5 md:p-10";
const PREL_OPS_PANEL_CLASS = "rounded-[30px] border border-black/10 bg-white/84 shadow-[0_26px_80px_rgba(17,17,17,0.08)] backdrop-blur";
const PREL_OPS_SOFT_PANEL_CLASS = "rounded-[26px] border border-black/10 bg-[#fbfbf7]/94";
const PREL_OPS_INPUT_CLASS = "rounded-full border border-black/10 bg-white px-4 py-3 text-[#111111] shadow-sm outline-none transition placeholder:text-black/35 focus:border-[#7c3aed] focus:ring-4 focus:ring-[#a78bfa]/30";
const PREL_OPS_SUBTLE_TEXT_CLASS = "text-black/62";
const PREL_OPS_BADGE_CLASS = "inline-flex rounded-full border border-black/10 bg-white/78 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-black/62";
const PREL_FOOTER_COPY = "Puerto Rico Esports League TM. Todos los derechos reservados.";

function fmt(value) {
  return new Intl.NumberFormat("es-PR").format(value || 0);
}

function fmtMaybe(value, hasData) {
  if (!hasData || value === null || value === undefined) return "—";
  return fmt(value);
}

function fmtDate(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return new Intl.DateTimeFormat("es-PR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function discordHandleLabel(...values) {
  return values.map((value) => String(value || "").trim()).find(Boolean) || "N/A";
}

function parseDisplayList(value) {
  return String(value || "")
    .split(/\r?\n|,|;|\|/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function fullName(row) {
  return [row?.first_name, row?.last_name].map((value) => String(value || "").trim()).filter(Boolean).join(" ").trim();
}

function buildTeamMemberList(team, assignedPlayers = []) {
  const members = [];
  const seen = new Set();

  const pushMember = (label, meta = {}) => {
    const normalized = String(label || "").trim();
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    members.push({ label: normalized, ...meta });
  };

  pushMember(team?.captain_name || fullName(team), { role: "Capitán" });
  parseDisplayList(team?.roster_core).forEach((label) => pushMember(label, { role: "Jugador" }));
  assignedPlayers.forEach((player) => {
    pushMember(fullName(player) || player?.primary_handle || player?.discord || "Free Agent", {
      role: "Free Agent asignado",
      entryId: player?.id || "",
    });
  });

  return members;
}

function capSummaryLabel(taken, cap, emptyLabel = "Sin límite") {
  if (!cap) return emptyLabel;
  return `${fmt(taken)} / ${fmt(cap)}`;
}

function capProgressPercent(taken, cap) {
  if (!cap) return 0;
  return Math.max(0, Math.min((taken / cap) * 100, 100));
}

function statusClasses(status, visualVariant = "admin") {
  const fallbackClass = visualVariant === "handshake"
    ? "border-black/10 bg-black/[0.04] text-black/65 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]"
    : "border-slate-300 bg-slate-100 text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]";
  switch (status) {
    case "confirmed":
      return "border-emerald-300 bg-emerald-50 text-emerald-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]";
    case "open":
    case "active":
    case "new":
      return "border-emerald-300 bg-emerald-50 text-emerald-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]";
    case "waitlist":
      return "border-amber-300 bg-amber-50 text-amber-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]";
    case "needs_players":
      return "border-sky-300 bg-sky-50 text-sky-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]";
    case "closed":
    case "disabled":
      return "border-rose-300 bg-rose-50 text-rose-800 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]";
    default:
      return fallbackClass;
  }
}

function formatStatusLabel(status) {
  const normalized = String(status || "").trim().toLowerCase();
  switch (normalized) {
    case "confirmed":
      return "Confirmed";
    case "active":
      return "Active";
    case "waitlist":
      return "Waitlist";
    case "needs_players":
      return "Needs Players";
    case "closed":
      return "Closed";
    case "assigned":
      return "Assigned";
    case "checked_in":
      return "Checked In";
    case "open":
      return "Open";
    case "new":
      return "New";
    case "disabled":
      return "Disabled";
    default:
      return normalized
        ? normalized
            .split("_")
            .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
            .join(" ")
        : "Unknown";
  }
}

function getEntryDisplayStatus(row) {
  const queueStatus = String(row?.queueStatus || row?.queue_status || "").trim().toLowerCase();
  const reviewState = String(row?.reviewState || row?.review_state || row?.status || "").trim().toLowerCase();
  const promotedStatus = String(row?.promoted_status || "").trim().toLowerCase();

  if (queueStatus === "needs_players") {
    return { status: "needs_players", label: formatStatusLabel("needs_players") };
  }
  if (queueStatus === "waitlist") {
    return { status: "waitlist", label: formatStatusLabel("waitlist") };
  }
  if (queueStatus === "closed") {
    return { status: "closed", label: formatStatusLabel("closed") };
  }
  if (queueStatus === "assigned") {
    return { status: "assigned", label: formatStatusLabel("assigned") };
  }
  if (reviewState === "confirmed" || promotedStatus === "confirmed") {
    return { status: "confirmed", label: formatStatusLabel("confirmed") };
  }
  if (queueStatus) {
    return { status: queueStatus, label: formatStatusLabel(queueStatus) };
  }
  return { status: reviewState || "new", label: formatStatusLabel(reviewState || "new") };
}

function QueuePill({ label, status, visualVariant = "admin" }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-3 ${visualVariant === "handshake" ? "py-1.5 text-[11px] tracking-[0.2em]" : "py-1 text-xs tracking-[0.16em]"} font-bold uppercase ${statusClasses(status, visualVariant)}`}>
      {label}
    </span>
  );
}

function CapacityMeter({ label, taken, cap, remaining, tone = "cyan", visualVariant = "admin" }) {
  const toneClasses = {
    cyan: "from-cyan-300 to-sky-500",
    violet: "from-violet-300 to-fuchsia-500",
    amber: "from-amber-300 to-orange-500",
    emerald: "from-emerald-300 to-teal-500",
  };
  const progress = capProgressPercent(taken, cap);

  return (
    <div className={visualVariant === "handshake" ? "rounded-[24px] border border-black/10 bg-white p-4 shadow-[0_10px_30px_rgba(17,17,17,0.05)]" : "rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm"}>
      <div className="flex items-center justify-between gap-3">
        <div className={visualVariant === "handshake" ? "text-[11px] font-semibold uppercase tracking-[0.22em] text-black/45" : "text-xs uppercase tracking-wide text-slate-500"}>{label}</div>
        <div className={visualVariant === "handshake" ? "text-xs text-black/45" : "text-xs text-slate-500"}>
          {cap ? `${Math.round(progress)}% ocupado` : "Sin límite"}
        </div>
      </div>
      <div className="mt-2 flex items-end justify-between gap-3">
        <div className={visualVariant === "handshake" ? "text-2xl font-black tracking-[-0.04em] text-[#111111]" : "text-2xl font-black text-slate-950"}>{capSummaryLabel(taken, cap)}</div>
        <div className={visualVariant === "handshake" ? "text-right text-xs text-black/56" : "text-right text-xs text-slate-600"}>
          {cap ? <>Quedan <strong>{fmt(remaining ?? 0)}</strong></> : "Cupo abierto"}
        </div>
      </div>
      <div className={visualVariant === "handshake" ? "mt-4 h-2.5 overflow-hidden rounded-full bg-black/8" : "mt-3 h-2 overflow-hidden rounded-full bg-slate-200"}>
        <div
          className={`h-full rounded-full bg-gradient-to-r ${toneClasses[tone] || toneClasses.cyan}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function StaticPanel({ children, className = "", visualVariant = "admin" }) {
  return (
    <div className={`${visualVariant === "handshake" ? PREL_OPS_PANEL_CLASS : PREL_ADMIN_PANEL_CLASS} ${className}`}>
      {children}
    </div>
  );
}

function ActionCardButton({ active = false, children, className = "", visualVariant = "admin", ...props }) {
  return (
    <button
      {...props}
      className={`${visualVariant === "handshake" ? "rounded-[26px] focus:ring-4 focus:ring-[#a78bfa]/40" : "rounded-2xl focus:ring-2 focus:ring-[#0f4cc9]/25"} border text-left transition duration-150 focus:outline-none ${
        active
          ? visualVariant === "handshake"
            ? "border-violet-300 bg-[linear-gradient(135deg,#ede9fe,#ffe4e6)] text-[#3b0764] shadow-[0_0_0_1px_rgba(124,58,237,0.08)_inset]"
            : "border-[#0f4cc9]/30 bg-[linear-gradient(135deg,rgba(15,76,201,0.11),rgba(239,68,68,0.07))] text-slate-950 shadow-[0_0_0_1px_rgba(15,76,201,0.10)_inset]"
          : visualVariant === "handshake"
            ? "border-black/10 bg-white/92 text-[#111111] hover:border-black/20 hover:bg-white hover:shadow-[0_16px_44px_rgba(17,17,17,0.08)]"
            : "border-slate-200 bg-white/92 text-slate-900 hover:border-[#0f4cc9]/25 hover:bg-[#f7fbff] hover:shadow-[0_12px_32px_rgba(15,23,42,0.10)]"
      } ${className}`}
    >
      {children}
    </button>
  );
}

function actionToneClasses(tone = "neutral", visualVariant = "admin") {
  if (visualVariant === "handshake") {
    switch (tone) {
      case "confirm":
        return "border-[#43f0a3]/55 bg-[linear-gradient(135deg,rgba(7,19,15,0.98),rgba(13,49,38,0.96))] text-[#8fffd1] hover:-translate-y-[1px] hover:border-[#5ff7b4]/70 hover:shadow-[0_14px_32px_rgba(67,240,163,0.20)]";
      case "waitlist":
        return "border-[#ff9b54]/55 bg-[linear-gradient(135deg,rgba(28,16,10,0.98),rgba(79,33,14,0.96))] text-[#ffd29b] hover:-translate-y-[1px] hover:border-[#ffb06f]/70 hover:shadow-[0_14px_32px_rgba(255,115,0,0.18)]";
      case "checkin":
        return "border-[#b56bff]/55 bg-[linear-gradient(135deg,rgba(26,12,40,0.98),rgba(87,28,91,0.96))] text-[#f0b7ff] hover:-translate-y-[1px] hover:border-[#ca89ff]/75 hover:shadow-[0_14px_32px_rgba(181,107,255,0.22)]";
      case "assign":
        return "border-[#ff6ea8]/55 bg-[linear-gradient(135deg,rgba(36,11,24,0.98),rgba(112,21,53,0.96))] text-[#ffc1db] hover:-translate-y-[1px] hover:border-[#ff89b8]/75 hover:shadow-[0_14px_32px_rgba(255,110,168,0.22)]";
      case "roster":
        return "border-[#6f86ff]/55 bg-[linear-gradient(135deg,rgba(16,19,42,0.98),rgba(34,45,108,0.96))] text-[#c7d2ff] hover:-translate-y-[1px] hover:border-[#8ca0ff]/70 hover:shadow-[0_14px_32px_rgba(111,134,255,0.20)]";
      case "danger":
        return "border-[#ff5d7a]/60 bg-[linear-gradient(135deg,rgba(34,7,13,0.98),rgba(120,16,38,0.98))] text-[#ffc1cb] hover:-translate-y-[1px] hover:border-[#ff7b93]/80 hover:shadow-[0_14px_32px_rgba(255,93,122,0.24)]";
      case "close":
        return "border-[#ff4d4d]/60 bg-[linear-gradient(135deg,rgba(36,8,8,0.98),rgba(134,20,20,0.98))] text-[#ffd0d0] hover:-translate-y-[1px] hover:border-[#ff7272]/80 hover:shadow-[0_14px_32px_rgba(255,77,77,0.24)]";
      default:
        return "border-black/12 bg-[linear-gradient(135deg,rgba(255,255,255,0.96),rgba(244,240,251,0.92))] text-black/72 hover:-translate-y-[1px] hover:border-black/20 hover:shadow-[0_14px_28px_rgba(17,17,17,0.08)]";
    }
  }

  switch (tone) {
    case "confirm":
      return "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100";
    case "waitlist":
      return "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100";
    case "checkin":
      return "border-[#0f4cc9]/25 bg-[#edf4ff] text-[#0f4cc9] hover:bg-[#dfeaff]";
    case "assign":
      return "border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100";
    case "roster":
      return "border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100";
    case "danger":
    case "close":
      return "border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100";
    default:
      return "border-slate-300 bg-white text-slate-800 hover:bg-slate-50";
  }
}

function ActionToneButton({ className = "", children, visualVariant = "admin", tone = "neutral", ...props }) {
  return (
    <button
      {...props}
      className={`${
        visualVariant === "handshake"
          ? "rounded-full px-4 py-2.5 text-[12px] tracking-[0.02em] shadow-[0_8px_22px_rgba(17,17,17,0.06)]"
          : "rounded-lg px-3 py-2 text-sm shadow-sm"
      } border font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${actionToneClasses(tone, visualVariant)} ${className}`}
    >
      {children}
    </button>
  );
}

function TournamentOpsFooter({ visualVariant = "admin" }) {
  return (
    <footer className={`${visualVariant === "handshake" ? "border-black/10 text-black/52" : "border-slate-200/80 text-slate-500"} border-t pt-6 text-center text-xs`}>
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-3">
        <img
          src="/tournament-ops-assets/prel-logo-v1.png"
          alt="Puerto Rico Esports League"
          className="h-10 w-auto object-contain opacity-90"
          loading="lazy"
          decoding="async"
        />
        <p>{PREL_FOOTER_COPY}</p>
      </div>
    </footer>
  );
}

function getGameBranding(game, fallbackLabel = "") {
  const key = String(game || "").trim();
  const preset = GAME_BRANDING[key];
  if (preset) return preset;
  const label = fallbackLabel || "este titulo";
  return {
    title: label,
    heroTitle: `Operación · ${label}`,
    description: `Aquí corre solo la operación de ${label}: registros, check-in, cupos y brackets de este título.`,
    logoSrc: "",
    logoAlt: label,
    logoClassName: "max-h-16 md:max-h-20 w-auto object-contain",
    decorSrc: "",
    decorAlt: label,
    decorClassName: "max-h-24 md:max-h-32 w-auto object-contain opacity-95",
  };
}

async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || "";
}

export default function AdminGamergyOps({
  apiPath = "/api/admin/gamergy-intake",
  bracketApiPath = "/api/tournament-ops/gamergy-brackets",
  enableFreeAgentTeamBuilder = false,
  enableExtendedBracketModes = false,
  badgeLabel = "Gamergy Ops",
  pageTitle = "Panel de Torneos",
  pageDescription = "Aquí puedes revisar registros, organizar equipos y jugadores, manejar listas de espera y llevar el check-in de cada juego de forma clara y rápida.",
  initialGame = "all",
  lockGameSelection = false,
  showGameSelectionScreen = false,
  gameRouteBase = "",
  visualVariant = "admin",
  primaryLinks = [
    { href: "/admin/dashboard", label: "Dashboard" },
    { href: "/admin/registros", label: "Registros PREL" },
  ],
} = {}) {
  const isHandshakeVariant = visualVariant === "handshake";
  const shellClass = isHandshakeVariant ? PREL_OPS_SHELL_CLASS : PREL_ADMIN_SHELL_CLASS;
  const subtleTextClass = isHandshakeVariant ? PREL_OPS_SUBTLE_TEXT_CLASS : PREL_ADMIN_SUBTLE_TEXT_CLASS;
  const badgeClass = isHandshakeVariant ? PREL_OPS_BADGE_CLASS : PREL_ADMIN_BADGE_CLASS;
  const inputClass = isHandshakeVariant ? PREL_OPS_INPUT_CLASS : PREL_ADMIN_INPUT_CLASS;
  const softPanelClass = isHandshakeVariant ? PREL_OPS_SOFT_PANEL_CLASS : PREL_ADMIN_SOFT_PANEL_CLASS;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [gameFilter, setGameFilter] = useState(initialGame || "all");
  const [search, setSearch] = useState("");
  const [actionBusy, setActionBusy] = useState("");
  const [controlDrafts, setControlDrafts] = useState({});
  const [workspaceTab, setWorkspaceTab] = useState("overview");
  const [selectedFreeAgentIds, setSelectedFreeAgentIds] = useState([]);
  const [teamDrafts, setTeamDrafts] = useState({});
  const [assignmentDrafts, setAssignmentDrafts] = useState({});
  const [manualTeamDraft, setManualTeamDraft] = useState({
    game: initialGame && initialGame !== "all" ? initialGame : "",
    teamName: "",
    captainName: "",
    captainDiscord: "",
    email: "",
    phone: "",
  });
  const [accessLinkEmail, setAccessLinkEmail] = useState("");
  const [accessLinkType, setAccessLinkType] = useState("auto");
  const [accessLinkMessage, setAccessLinkMessage] = useState("");

  async function load() {
    setLoading(true);
    setError("");

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error("No pudimos validar tu acceso. Recarga la página o vuelve a entrar.");
      }
      const res = await fetch(apiPath, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) {
        const authMessage = res.status === 401 || res.status === 403
          ? "Tu acceso venció o no se confirmó bien en este navegador. Cierra sesión y vuelve a entrar."
          : null;
        throw new Error(authMessage || json.error || "No se pudo cargar el panel.");
      }
      setPayload(json);
      setControlDrafts(Object.fromEntries((json.controls || []).map((item) => [item.game, {
        game: item.game,
        label: item.label,
        teamSize: item.teamSize,
        soloStatus: item.soloStatus,
        teamStatus: item.teamStatus,
        totalCap: item.totalCap ?? "",
        soloCap: item.soloCap ?? "",
        teamCap: item.teamCap ?? "",
        freeAgentCap: item.freeAgentCap ?? "",
        waitlistEnabled: item.waitlistEnabled,
        notes: item.notes || "",
      }])));
    } catch (err) {
      setError(err.message || "No se pudo cargar el panel.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setGameFilter(initialGame || "all");
  }, [initialGame]);

  useEffect(() => {
    if (initialGame && initialGame !== "all") {
      setManualTeamDraft((prev) => ({
        ...prev,
        game: prev.game || initialGame,
      }));
    }
  }, [initialGame]);

  const byGame = payload?.byGame || [];
  const teams = payload?.teams || [];
  const freeAgents = payload?.freeAgents || [];
  const assignedFreeAgents = payload?.assignedFreeAgents || [];
  const soloPlayers = payload?.soloPlayers || [];
  const recent = payload?.recent || [];
  const overview = payload?.overview || {};
  const controls = payload?.controls || [];
  const hasPayload = Boolean(payload);
  const accessContext = payload?.admin || payload?.access || null;
  const canManageTournamentAccess = accessContext?.source === "admin_users";
  const selectedGameMeta = gameFilter === "all"
    ? null
    : byGame.find((item) => item.game === gameFilter) || null;
  const heroBranding = useMemo(() => {
    if (!lockGameSelection) return null;
    return getGameBranding(selectedGameMeta?.game || initialGame, selectedGameMeta?.label);
  }, [initialGame, lockGameSelection, selectedGameMeta]);
  const displayPageTitle = heroBranding?.heroTitle || pageTitle;
  const displayPageDescription = heroBranding?.description || pageDescription;

  const normalizedSearch = search.trim().toLowerCase();

  const filteredTeams = useMemo(() => {
    return teams.filter((row) => {
      if (gameFilter !== "all" && row.game !== gameFilter) return false;
      if (!normalizedSearch) return true;
      const haystack = [row.team_name, row.captain_name, row.email, row.discord, row.primary_handle].join(" ").toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [teams, gameFilter, normalizedSearch]);

  const filteredFreeAgents = useMemo(() => {
    return freeAgents.filter((row) => {
      if (gameFilter !== "all" && row.game !== gameFilter) return false;
      if (!normalizedSearch) return true;
      const haystack = [row.first_name, row.last_name, row.email, row.discord, row.primary_handle].join(" ").toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [freeAgents, gameFilter, normalizedSearch]);

  const filteredSoloPlayers = useMemo(() => {
    return soloPlayers.filter((row) => {
      if (gameFilter !== "all" && row.game !== gameFilter) return false;
      if (!normalizedSearch) return true;
      const haystack = [row.first_name, row.last_name, row.email, row.discord, row.primary_handle].join(" ").toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [soloPlayers, gameFilter, normalizedSearch]);

  const filteredByGame = useMemo(() => {
    if (gameFilter === "all") return byGame;
    return byGame.filter((item) => item.game === gameFilter);
  }, [byGame, gameFilter]);

  const filteredControls = useMemo(() => {
    if (gameFilter === "all") return controls;
    return controls.filter((item) => item.game === gameFilter);
  }, [controls, gameFilter]);

  const filteredAssignedFreeAgents = useMemo(() => {
    if (gameFilter === "all") return assignedFreeAgents;
    return assignedFreeAgents.filter((row) => row.game === gameFilter);
  }, [assignedFreeAgents, gameFilter]);

  const assignableTeamsByGame = useMemo(() => {
    return teams.reduce((acc, team) => {
      if (!team?.game) return acc;
      if (!acc[team.game]) acc[team.game] = [];
      acc[team.game].push(team);
      return acc;
    }, {});
  }, [teams]);

  const assignedFreeAgentsByTeamId = useMemo(() => {
    return assignedFreeAgents.reduce((acc, row) => {
      if (!row?.assignedTeamEntryId) return acc;
      if (!acc[row.assignedTeamEntryId]) acc[row.assignedTeamEntryId] = [];
      acc[row.assignedTeamEntryId].push(row);
      return acc;
    }, {});
  }, [assignedFreeAgents]);

  const selectedFreeAgentRows = useMemo(
    () => freeAgents.filter((row) => selectedFreeAgentIds.includes(row.id)),
    [freeAgents, selectedFreeAgentIds]
  );
  const selectedFreeAgentGame = useMemo(() => {
    const games = [...new Set(selectedFreeAgentRows.map((row) => row.game).filter(Boolean))];
    return games.length === 1 ? games[0] : "";
  }, [selectedFreeAgentRows]);

  const filteredRecent = useMemo(() => {
    if (gameFilter === "all") return recent;
    return recent.filter((row) => row.game === gameFilter);
  }, [recent, gameFilter]);

  const visibleOverview = useMemo(() => {
    if (gameFilter === "all") return overview;
    if (!selectedGameMeta) {
      return {
        totalEntries: 0,
        totalTeams: 0,
        totalSoloPlayers: 0,
        totalFreeAgents: 0,
        totalAssignedFreeAgents: 0,
        totalWaitlist: 0,
        totalCheckedIn: 0,
      };
    }

    return {
      totalEntries: selectedGameMeta.totalEntries || 0,
      totalTeams: selectedGameMeta.teamEntries || 0,
      totalSoloPlayers: selectedGameMeta.soloEntries || 0,
      totalFreeAgents: selectedGameMeta.freeAgentEntries || 0,
      totalAssignedFreeAgents: selectedGameMeta.assignedEntries || 0,
      totalWaitlist: selectedGameMeta.waitlistEntries || 0,
      totalCheckedIn: selectedGameMeta.checkedInEntries || 0,
    };
  }, [gameFilter, overview, selectedGameMeta]);

  async function performAction(action, body, busyKey) {
    setError("");
    setActionBusy(busyKey);
    try {
      const token = await getAccessToken();
      const res = await fetch(apiPath, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action, ...body }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || "No se pudo completar la acción.");
      setPayload(json);
      setControlDrafts(Object.fromEntries((json.controls || []).map((item) => [item.game, {
        game: item.game,
        label: item.label,
        teamSize: item.teamSize,
        soloStatus: item.soloStatus,
        teamStatus: item.teamStatus,
        totalCap: item.totalCap ?? "",
        soloCap: item.soloCap ?? "",
        teamCap: item.teamCap ?? "",
        freeAgentCap: item.freeAgentCap ?? "",
        waitlistEnabled: item.waitlistEnabled,
        notes: item.notes || "",
      }])));
    } catch (err) {
      setError(err.message || "No se pudo completar la acción.");
    } finally {
      setActionBusy("");
    }
  }

  async function deleteRegistration(row) {
    const label = row.entryType === "team"
      ? row.team_name || row.captain_name || "este equipo"
      : `${row.first_name || ""} ${row.last_name || ""}`.trim() || row.email || "este registro";

    const confirmed = window.confirm(`Esto borrara por completo el registro de ${label}. Esta accion no se puede deshacer desde el panel. Quieres continuar?`);
    if (!confirmed) return;

    await performAction("delete_entry", { entryId: row.id }, `delete:${row.id}`);
  }

  function toggleFreeAgentSelection(entryId) {
    setSelectedFreeAgentIds((prev) =>
      prev.includes(entryId) ? prev.filter((item) => item !== entryId) : [...prev, entryId]
    );
  }

  function updateTeamDraft(game, value) {
    setTeamDrafts((prev) => ({
      ...prev,
      [game]: value,
    }));
  }

  function updateAssignmentDraft(entryId, teamId) {
    setAssignmentDrafts((prev) => ({
      ...prev,
      [entryId]: teamId,
    }));
  }

  function updateManualTeamDraft(key, value) {
    setManualTeamDraft((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  async function sendTournamentAccessLink() {
    const email = accessLinkEmail.trim().toLowerCase();
    if (!email) {
      setError("Escribe un email válido para enviar el enlace de acceso.");
      return;
    }

    setError("");
    setAccessLinkMessage("");
    setActionBusy("admin:access-link");
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error("No pudimos validar tu sesión admin para enviar el enlace.");
      }

      const res = await fetch("/api/admin/gamergy-intake", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "send_tournament_access_link",
          email,
          linkType: accessLinkType === "auto" ? undefined : accessLinkType,
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) {
        throw new Error(json.error || "No se pudo enviar el enlace de acceso.");
      }

      setAccessLinkMessage(
        `Enlace enviado a ${json.email}. Tipo usado: ${json.linkType === "recovery" ? "recovery" : "magic link"}.`
      );
      setAccessLinkEmail("");
      setAccessLinkType("auto");
    } catch (err) {
      setError(err.message || "No se pudo enviar el enlace de acceso.");
    } finally {
      setActionBusy("");
    }
  }

  async function createTeamFromSelectedFreeAgents() {
    if (!selectedFreeAgentRows.length || !selectedFreeAgentGame) return;
    const teamName = String(teamDrafts[selectedFreeAgentGame] || "").trim();
    if (!teamName) {
      setError("Escribe el nombre del equipo nuevo antes de crearlo.");
      return;
    }

    await performAction(
      "create_team_from_free_agents",
      {
        teamName,
        freeAgentEntryIds: selectedFreeAgentRows.map((row) => row.id),
      },
      `create-team:${selectedFreeAgentGame}`
    );
    setSelectedFreeAgentIds([]);
    setTeamDrafts((prev) => ({
      ...prev,
      [selectedFreeAgentGame]: "",
    }));
  }

  async function assignFreeAgentToSelectedTeam(row) {
    const teamId = assignmentDrafts[row.id];
    if (!teamId) {
      setError("Escoge el equipo destino antes de asignar el jugador.");
      return;
    }

    await performAction(
      "assign_free_agent",
      { freeAgentEntryId: row.id, teamEntryId: teamId },
      `assign:${row.id}:${teamId}`
    );

    setAssignmentDrafts((prev) => {
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
  }

  async function unassignFreeAgentFromTeam(row) {
    await performAction(
      "unassign_free_agent",
      { freeAgentEntryId: row.id },
      `unassign:${row.id}`
    );
  }

  async function createManualTeamEntry() {
    const game = manualTeamDraft.game || (gameFilter !== "all" ? gameFilter : "");
    if (!game) {
      setError("Escoge el juego antes de crear un equipo manual.");
      return;
    }

    await performAction(
      "create_manual_team",
      {
        game,
        teamName: manualTeamDraft.teamName,
        captainName: manualTeamDraft.captainName,
        captainDiscord: manualTeamDraft.captainDiscord,
        email: manualTeamDraft.email,
        phone: manualTeamDraft.phone,
      },
      `manual-team:${game}`
    );

    setManualTeamDraft((prev) => ({
      ...prev,
      game,
      teamName: "",
      captainName: "",
      captainDiscord: "",
      email: "",
      phone: "",
    }));
  }

  async function downloadCsvExport() {
    setError("");
    setActionBusy("download:csv");
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error("No pudimos validar tu acceso para exportar el CSV.");
      }

      const params = new URLSearchParams({ format: "csv" });
      if (gameFilter && gameFilter !== "all") {
        params.set("game", gameFilter);
      }

      const res = await fetch(`${apiPath}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        let message = "No se pudo exportar el CSV.";
        try {
          const json = await res.clone().json();
          message = json?.error || message;
        } catch {
          // Ignore JSON parse errors and keep fallback message.
        }
        throw new Error(message);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const filenameGame = gameFilter && gameFilter !== "all" ? `-${gameFilter}` : "";
      const filenameDate = new Date().toISOString().slice(0, 10);
      const link = document.createElement("a");
      link.href = url;
      link.download = `gamergy-intake${filenameGame}-${filenameDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "No se pudo exportar el CSV.");
    } finally {
      setActionBusy("");
    }
  }

  function updateControlDraft(game, key, value) {
    setControlDrafts((prev) => ({
      ...prev,
      [game]: {
        ...(prev[game] || {}),
        [key]: value,
      },
    }));
  }

  const gameSelectionCards = byGame.map((item) => {
    const href = gameRouteBase ? `${gameRouteBase}/${encodeURIComponent(item.game)}` : "#";
    const branding = getGameBranding(item.game, item.label);
    return (
      <a
        key={item.game}
        href={href}
        className={`group relative overflow-hidden p-4 transition sm:p-5 ${
          isHandshakeVariant
            ? "rounded-[30px] border border-black/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(248,244,255,0.98))] hover:border-violet-200 hover:shadow-[0_20px_56px_rgba(17,17,17,0.10)]"
            : "rounded-3xl border border-slate-200 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(246,249,255,0.98))] hover:border-[#0f4cc9]/30 hover:bg-[linear-gradient(180deg,rgba(255,255,255,1),rgba(239,245,255,1))] hover:shadow-[0_18px_50px_rgba(15,23,42,0.12)]"
        }`}
      >
        <div className={`pointer-events-none absolute inset-y-0 right-0 w-36 ${isHandshakeVariant ? "bg-[radial-gradient(circle_at_right,rgba(124,58,237,0.18),transparent_65%)]" : "bg-[radial-gradient(circle_at_right,rgba(15,76,201,0.10),transparent_65%)]"}`} />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className={isHandshakeVariant ? "text-[11px] uppercase tracking-[0.28em] text-black/45" : "text-[11px] uppercase tracking-[0.28em] text-[#0f4cc9]/80"}>Título</div>
            <h2 className={`mt-2 text-xl font-black sm:text-2xl ${isHandshakeVariant ? "tracking-[-0.03em] text-[#111111]" : "text-slate-950"}`}>{item.label}</h2>
            <p className={`mt-2 text-sm ${subtleTextClass}`}>
              Abre el workspace operativo de este juego para manejar registros, check-in y brackets.
            </p>
          </div>
          <div className="flex flex-row items-center justify-between gap-3 sm:flex-col sm:items-end">
            <div className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] ${isHandshakeVariant ? "border border-violet-200 bg-[linear-gradient(135deg,#ede9fe,#ffe4e6)] text-[#4c1d95]" : "border border-[#0f4cc9]/15 bg-[#0f4cc9]/8 text-[#0f4cc9]"}`}>
              Entrar
            </div>
            {branding.decorSrc ? (
              <img
                src={branding.decorSrc}
                alt={branding.decorAlt}
                className="max-h-14 w-auto object-contain opacity-90 transition duration-200 group-hover:scale-[1.03] sm:max-h-20"
                loading="lazy"
                decoding="async"
              />
            ) : null}
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <div className={`${softPanelClass} p-3`}>
            <div className={isHandshakeVariant ? "text-[11px] uppercase tracking-[0.22em] text-black/45" : "text-xs uppercase tracking-wide text-slate-500"}>Registros</div>
            <div className="mt-2 text-2xl font-black">{fmt(item.totalEntries)}</div>
          </div>
          <div className={`${softPanelClass} p-3`}>
            <div className={isHandshakeVariant ? "text-[11px] uppercase tracking-[0.22em] text-black/45" : "text-xs uppercase tracking-wide text-slate-500"}>Estado</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <QueuePill label={`Jugador · ${item.playerStatus}`} status={item.playerStatus} visualVariant={visualVariant} />
              <QueuePill label={`Equipo · ${item.teamStatus}`} status={item.teamStatus} visualVariant={visualVariant} />
            </div>
          </div>
          <div className={`${softPanelClass} p-3`}>
            <div className={isHandshakeVariant ? "text-[11px] uppercase tracking-[0.22em] text-black/45" : "text-xs uppercase tracking-wide text-slate-500"}>Check-in</div>
            <div className="mt-2 text-2xl font-black">{fmt(item.checkedInEntries || 0)}</div>
          </div>
        </div>

        <div className={`mt-4 flex flex-col gap-2 text-sm ${subtleTextClass} sm:flex-row sm:items-center sm:justify-between`}>
          <span>Cupo general: <strong>{capSummaryLabel(item.totalEntries, item.totalCap)}</strong></span>
          <span className={isHandshakeVariant ? "text-black transition group-hover:text-black/68" : "text-[#0f4cc9] transition group-hover:text-[#0a3b9d]"}>Abrir panel del juego</span>
        </div>
      </a>
    );
  });

  if (showGameSelectionScreen) {
    return (
      <div className={shellClass}>
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="max-w-4xl">
              <img
                src="/tournament-ops-assets/prel-logo-v1.png"
                alt="Puerto Rico Esports League"
                className="h-12 w-auto object-contain sm:h-14"
                loading="eager"
                decoding="async"
              />
              <div className={`mt-4 ${badgeClass}`}>{badgeLabel}</div>
              <h1 className={`mt-4 font-black md:text-6xl ${isHandshakeVariant ? "text-4xl tracking-[-0.05em]" : "text-3xl md:text-5xl"}`}>{pageTitle}</h1>
              <p className={`mt-2 max-w-3xl ${subtleTextClass}`}>{pageDescription}</p>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
              {primaryLinks.map((link) => (
                <a key={link.href} href={link.href} className={isHandshakeVariant ? "rounded-full border border-black/10 bg-white/75 px-4 py-2.5 text-center text-black/72 hover:border-black/20 hover:bg-white" : "rounded-lg border border-slate-300 bg-white/75 px-4 py-2 text-center text-slate-800 hover:bg-white"}>
                  {link.label}
                </a>
              ))}
              <button onClick={load} className={isHandshakeVariant ? "rounded-full border border-black bg-[#111111] px-4 py-2.5 text-white hover:bg-black" : "rounded-lg border border-[#0f4cc9]/20 bg-white/75 px-4 py-2 text-[#0f4cc9] hover:bg-[#edf4ff]"}>Refrescar</button>
            </div>
          </div>

          {error ? <div className="rounded-2xl border border-rose-300 bg-rose-50 p-4 text-rose-800">{error}</div> : null}

          <StaticPanel className="p-5 md:p-6" visualVariant={visualVariant}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold">Escoge el torneo que vas a correr ahora</h2>
                <p className={`mt-1 text-sm ${subtleTextClass}`}>
                  El staff entra primero por juego. Así cada persona trabaja en un solo título y evita ver todo mezclado.
                </p>
              </div>
              <div className="rounded-full border border-slate-200 bg-white/70 px-3 py-2 text-xs uppercase tracking-[0.22em] text-slate-600">
                {loading ? "Cargando titulos" : `${byGame.length} titulos activos`}
              </div>
            </div>
          </StaticPanel>

          {loading ? (
            <StaticPanel className="p-4 text-slate-600 sm:p-5" visualVariant={visualVariant}>Cargando juegos...</StaticPanel>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {gameSelectionCards}
            </div>
          )}

          {!loading && !byGame.length ? (
            <StaticPanel className="p-5 text-slate-600" visualVariant={visualVariant}>
              No encontramos juegos configurados todavía en este panel.
            </StaticPanel>
          ) : null}

          <TournamentOpsFooter visualVariant={visualVariant} />
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col gap-4 xl:grid xl:grid-cols-[minmax(0,1fr)_auto] xl:items-start xl:gap-6">
            <div className="max-w-4xl">
            <div className={badgeClass}>{badgeLabel}</div>
            <h1 className={`mt-4 font-black md:text-6xl ${isHandshakeVariant ? "text-4xl tracking-[-0.05em]" : "text-3xl md:text-5xl"}`}>{displayPageTitle}</h1>
            <p className={`mt-2 max-w-3xl ${subtleTextClass}`}>
              {displayPageDescription}
            </p>
          </div>
          <div className="flex w-full flex-col gap-3 xl:w-auto xl:min-w-[320px] xl:items-end">
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap xl:w-auto xl:justify-end">
              {primaryLinks.map((link) => (
                <a key={link.href} href={link.href} className={isHandshakeVariant ? "rounded-full border border-black/10 bg-white/75 px-4 py-2.5 text-center text-black/72 hover:border-black/20 hover:bg-white" : "rounded-lg border border-slate-300 bg-white/75 px-4 py-2 text-center text-slate-800 hover:bg-white"}>
                  {link.label}
                </a>
              ))}
              <button onClick={load} className={isHandshakeVariant ? "rounded-full border border-black bg-[#111111] px-4 py-2.5 text-white hover:bg-black" : "rounded-lg border border-[#0f4cc9]/20 bg-white/75 px-4 py-2 text-[#0f4cc9] hover:bg-[#edf4ff]"}>Refrescar</button>
            </div>
            {heroBranding?.logoSrc || heroBranding?.decorSrc ? (
              <div className="flex w-full justify-start xl:justify-end">
                <div className={`flex w-full max-w-[360px] items-center justify-between gap-4 overflow-hidden px-4 py-4 sm:px-5 ${isHandshakeVariant ? "rounded-[30px] border border-black/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.92),rgba(248,249,242,0.98),rgba(233,254,255,0.9))] shadow-[0_18px_50px_rgba(17,17,17,0.10)]" : "rounded-3xl border border-slate-200/80 bg-[linear-gradient(135deg,rgba(255,255,255,0.92),rgba(238,244,255,0.98),rgba(255,241,241,0.96))] shadow-[0_18px_50px_rgba(15,23,42,0.10)]"}`}>
                  {heroBranding?.logoSrc ? (
                    <img
                      src={heroBranding.logoSrc}
                      alt={heroBranding.logoAlt}
                      className={`${heroBranding.logoClassName} shrink min-w-0`}
                      loading="eager"
                      decoding="async"
                    />
                  ) : null}
                  {heroBranding?.decorSrc ? (
                    <img
                      src={heroBranding.decorSrc}
                      alt={heroBranding.decorAlt}
                      className={`${heroBranding.decorClassName} shrink-0`}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {error ? <div className="rounded-2xl border border-rose-300 bg-rose-50 p-4 text-rose-800">{error}</div> : null}

        <StaticPanel className="p-4 md:p-5" visualVariant={visualVariant}>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-xl font-bold">
                {lockGameSelection
                  ? `Workspace de ${selectedGameMeta?.label || heroBranding?.title || "este juego"}`
                  : "Trabaja por juego y por tarea"}
              </h2>
              <p className={`mt-1 text-sm ${subtleTextClass}`}>
                {lockGameSelection
                  ? "Aquí solo ves la operación del juego seleccionado. Si necesitas otro título, vuelve a la pantalla inicial y cambia de workspace."
                  : "Para que el staff no vea todo mezclado, primero escoge el juego que vas a atender y despues entra al area de trabajo correcta."}
              </p>
            </div>
            <div className={`flex flex-col gap-2 ${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-white/78 px-4 py-3 text-[#111111] shadow-[0_10px_24px_rgba(17,17,17,0.05)]" : "rounded-2xl border border-slate-200/80 bg-white/88 px-4 py-3 text-slate-700 shadow-sm"} sm:flex-row sm:items-center sm:justify-between lg:min-w-[23rem] lg:max-w-[28rem]`}>
              <div className="min-w-0">
                <div className={isHandshakeVariant ? "text-[11px] font-semibold uppercase tracking-[0.22em] text-black/45" : "text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500"}>
                  Juego actual
                </div>
                <div className={isHandshakeVariant ? "mt-1 text-sm font-semibold leading-tight text-[#111111] sm:text-[15px]" : "mt-1 text-sm font-semibold leading-tight text-slate-900 sm:text-[15px]"}>
                  {selectedGameMeta?.label || heroBranding?.title || "Todos los juegos"}
                </div>
              </div>
              {lockGameSelection && gameRouteBase ? (
                <a
                  href={gameRouteBase}
                  className={`${isHandshakeVariant ? "border-violet-300/70 bg-[linear-gradient(135deg,#f5f3ff,#ffe4e6)] text-[#4338ca] hover:opacity-90" : "border-[#0f4cc9]/20 bg-[#0f4cc9]/5 text-[#0f4cc9] hover:bg-[#0f4cc9]/10"} shrink-0 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em]`}
                >
                  Cambiar juego
                </a>
              ) : null}
            </div>
          </div>

          {!lockGameSelection ? (
            <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
              <ActionCardButton
                onClick={() => setGameFilter("all")}
                active={gameFilter === "all"}
                visualVariant={visualVariant}
                className="shrink-0 rounded-full px-4 py-2 text-sm"
              >
                Todos los juegos
              </ActionCardButton>
              {byGame.map((item) => (
                <ActionCardButton
                  key={item.game}
                  onClick={() => setGameFilter(item.game)}
                  active={gameFilter === item.game}
                  visualVariant={visualVariant}
                  className="shrink-0 rounded-full px-4 py-2 text-sm"
                >
                  {item.label}
                </ActionCardButton>
              ))}
            </div>
          ) : null}

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {[
              ["overview", "Resumen", "Cupos, estados y ajustes del juego"],
              ["registrations", "Registros", "Equipos, jugadores y check-in"],
              ["brackets", "Brackets", "Rondas, pairings y resultados"],
            ].map(([key, label, description]) => (
              <ActionCardButton
                key={key}
                onClick={() => setWorkspaceTab(key)}
                active={workspaceTab === key}
                visualVariant={visualVariant}
                className="p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="font-semibold">{label}</div>
                  <div className="text-[10px] uppercase tracking-[0.22em] opacity-70">Abrir</div>
                </div>
                <div className="mt-1 text-sm opacity-90">{description}</div>
              </ActionCardButton>
            ))}
          </div>
        </StaticPanel>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-7">
          {[
            ["Total registrados", visibleOverview.totalEntries],
            ["Equipos", visibleOverview.totalTeams],
            ["Individuales", visibleOverview.totalSoloPlayers],
            ["Sin equipo", visibleOverview.totalFreeAgents],
            ["Asignados a equipo", visibleOverview.totalAssignedFreeAgents],
            ["Lista de espera", visibleOverview.totalWaitlist],
            ["Check-in", visibleOverview.totalCheckedIn],
          ].map(([label, value]) => (
            <StaticPanel key={label} className={`${isHandshakeVariant ? "min-h-[124px]" : "min-h-[112px]"} p-4`} visualVariant={visualVariant}>
              <div className="flex h-full flex-col">
                <div
                  className={
                    isHandshakeVariant
                      ? "min-h-[2.5rem] text-[11px] uppercase tracking-[0.22em] text-black/45"
                      : "min-h-[2rem] text-xs uppercase tracking-wide text-slate-500"
                  }
                >
                  {label}
                </div>
                <div className={`mt-3 flex flex-1 items-center justify-center text-center font-black ${isHandshakeVariant ? "text-3xl tracking-[-0.05em] text-[#111111]" : "text-3xl text-slate-950"}`}>
                  {loading ? "..." : fmtMaybe(value, hasPayload)}
                </div>
              </div>
            </StaticPanel>
          ))}
        </div>

        {!loading && !hasPayload ? (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-800">
            Los contadores no cargaron data real en este navegador. Si ves ceros o tarjetas vacías en mobile, el problema es la sesión/API, no necesariamente los registros.
          </div>
        ) : null}

        {workspaceTab === "overview" ? (
          <>
        {canManageTournamentAccess ? (
          <StaticPanel className="p-4 md:p-5" visualVariant={visualVariant}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-3xl">
                <h2 className="text-xl font-bold">Recuperar acceso de staff</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Esta herramienta solo aparece para cuentas de <code>admin_users</code>. Úsala para enviar un enlace directo a staff aprobado de Tournament Ops sin depender del flujo normal cuando se tranca por rate limit.
                </p>
              </div>
              <div className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] ${isHandshakeVariant ? "border border-violet-200 bg-[linear-gradient(135deg,#ede9fe,#ffe4e6)] text-[#4c1d95]" : "border border-[#0f4cc9]/15 bg-[#0f4cc9]/8 text-[#0f4cc9]"}`}>
                Solo admin
              </div>
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(180px,0.5fr)_auto] lg:items-end">
              <label className="text-sm text-slate-700">
                Email del staff
                <input
                  value={accessLinkEmail}
                  onChange={(e) => setAccessLinkEmail(e.target.value)}
                  placeholder="usuario@correo.com"
                  className={`mt-1 w-full ${inputClass}`}
                />
              </label>
              <label className="text-sm text-slate-700">
                Tipo de enlace
                <select
                  value={accessLinkType}
                  onChange={(e) => setAccessLinkType(e.target.value)}
                  className={`mt-1 w-full ${inputClass}`}
                >
                  <option value="auto">Auto</option>
                  <option value="recovery">Recovery</option>
                  <option value="magiclink">Magic link</option>
                </select>
              </label>
              <ActionToneButton
                onClick={sendTournamentAccessLink}
                disabled={actionBusy === "admin:access-link"}
                visualVariant={visualVariant}
                tone="assign"
                className="w-full lg:w-auto"
              >
                {actionBusy === "admin:access-link" ? "Enviando..." : "Enviar enlace"}
              </ActionToneButton>
            </div>

            {accessLinkMessage ? (
              <div className="mt-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">
                {accessLinkMessage}
              </div>
            ) : null}
          </StaticPanel>
        ) : null}

        <StaticPanel className="p-4 md:p-5">
          <div className="mb-4">
            <h2 className="text-xl font-bold">Ajustes del juego seleccionado</h2>
            <p className="mt-1 text-sm text-slate-600">Desde aquí puedes abrir o cerrar registros, activar lista de espera y ajustar cupos sin mezclarte con otros juegos.</p>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {filteredControls.map((item) => {
              const draft = controlDrafts[item.game] || {};
              return (
                <StaticPanel key={item.game} visualVariant={visualVariant} className={`${isHandshakeVariant ? "bg-[linear-gradient(180deg,rgba(248,244,255,0.92),rgba(255,246,248,0.9))]" : "bg-[#f8fbff]"} p-4 space-y-3`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold">{item.label}</h3>
                      <p className="text-xs uppercase tracking-wide text-slate-500">{item.game}</p>
                    </div>
                    <button
                      onClick={() => performAction("update_control", draft, `control:${item.game}`)}
                      disabled={actionBusy === `control:${item.game}`}
                      className="rounded-lg bg-[#0f4cc9] px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
                    >
                      Guardar
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="text-sm text-slate-700">
                      Individual
                      <select value={draft.soloStatus ?? ""} onChange={(e) => updateControlDraft(item.game, "soloStatus", e.target.value)} className={`mt-1 w-full ${inputClass}`}>
                        <option value="open">Abierto</option>
                        <option value="waitlist">Lista de espera</option>
                        <option value="closed">Cerrado</option>
                        <option value="disabled">Desactivado</option>
                      </select>
                    </label>
                    <label className="text-sm text-slate-700">
                      Equipos
                      <select value={draft.teamStatus ?? ""} onChange={(e) => updateControlDraft(item.game, "teamStatus", e.target.value)} className={`mt-1 w-full ${inputClass}`}>
                        <option value="open">Abierto</option>
                        <option value="waitlist">Lista de espera</option>
                        <option value="closed">Cerrado</option>
                        <option value="disabled">Desactivado</option>
                      </select>
                    </label>
                    <label className="text-sm text-slate-700">
                      Tamaño del equipo
                      <input value={draft.teamSize ?? ""} onChange={(e) => updateControlDraft(item.game, "teamSize", e.target.value)} className={`mt-1 w-full ${inputClass}`} />
                    </label>
                    <label className="flex items-center gap-2 pt-7 text-sm text-slate-700">
                      <input type="checkbox" checked={Boolean(draft.waitlistEnabled)} onChange={(e) => updateControlDraft(item.game, "waitlistEnabled", e.target.checked)} />
                      Activar lista de espera
                    </label>
                    <label className="text-sm text-slate-700">
                      Cupo total
                      <input value={draft.totalCap ?? ""} onChange={(e) => updateControlDraft(item.game, "totalCap", e.target.value)} className={`mt-1 w-full ${inputClass}`} />
                    </label>
                    <label className="text-sm text-slate-700">
                      Cupo individual
                      <input value={draft.soloCap ?? ""} onChange={(e) => updateControlDraft(item.game, "soloCap", e.target.value)} className={`mt-1 w-full ${inputClass}`} />
                    </label>
                    <label className="text-sm text-slate-700">
                      Cupo de equipos
                      <input value={draft.teamCap ?? ""} onChange={(e) => updateControlDraft(item.game, "teamCap", e.target.value)} className={`mt-1 w-full ${inputClass}`} />
                    </label>
                    <label className="text-sm text-slate-700">
                      Cupo sin equipo
                      <input value={draft.freeAgentCap ?? ""} onChange={(e) => updateControlDraft(item.game, "freeAgentCap", e.target.value)} className={`mt-1 w-full ${inputClass}`} />
                    </label>
                  </div>
                  <label className="block text-sm text-slate-700">
                    Nota para el staff
                    <textarea value={draft.notes ?? ""} onChange={(e) => updateControlDraft(item.game, "notes", e.target.value)} className={`mt-1 min-h-[88px] w-full ${inputClass}`} />
                  </label>
                </StaticPanel>
              );
            })}
          </div>
          {!filteredControls.length ? <p className="text-sm text-slate-400">No hay controles para este juego todavía.</p> : null}
        </StaticPanel>

        <StaticPanel className="p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-xl font-bold">Capacidad y estado</h2>
              <p className="text-sm text-slate-600">Aquí ves rápido qué juego sigue abierto, cuál ya llenó cupo y cuál necesita pasar a lista de espera.</p>
            </div>
            <div className="flex w-full flex-wrap gap-2 lg:w-auto">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar capitán, equipo, email o Discord..."
                className={`w-full lg:w-[22rem] ${inputClass}`}
              />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
            {filteredByGame.map((item) => (
              <StaticPanel key={item.game} visualVariant={visualVariant} className={`${isHandshakeVariant ? "bg-[linear-gradient(180deg,rgba(248,244,255,0.92),rgba(255,246,248,0.9))]" : "bg-[#f8fbff]"} p-4`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-lg font-bold">{item.label}</h3>
                    <p className="text-sm text-slate-600">{item.control.notes || "Sin nota operativa."}</p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <QueuePill label={`Jugador · ${item.playerStatus}`} status={item.playerStatus} />
                    <QueuePill label={`Equipo · ${item.teamStatus}`} status={item.teamStatus} />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div className={`${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-white/90 p-3 shadow-[0_10px_24px_rgba(17,17,17,0.05)]" : "rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm"}`}>
                    <div className="text-xs uppercase text-slate-500">Registros</div>
                    <div className="mt-1 text-2xl font-black text-slate-950">{fmt(item.totalEntries)}</div>
                  </div>
                  <div className={`${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-white/90 p-3 shadow-[0_10px_24px_rgba(17,17,17,0.05)]" : "rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm"}`}>
                    <div className="text-xs uppercase text-slate-500">Equipos</div>
                    <div className="mt-1 text-2xl font-black text-slate-950">{fmt(item.teamEntries)}</div>
                  </div>
                  <div className={`${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-white/90 p-3 shadow-[0_10px_24px_rgba(17,17,17,0.05)]" : "rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm"}`}>
                    <div className="text-xs uppercase text-slate-500">Sin equipo</div>
                    <div className="mt-1 text-2xl font-black text-slate-950">{fmt(item.freeAgentEntries)}</div>
                  </div>
                  <div className={`${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-white/90 p-3 shadow-[0_10px_24px_rgba(17,17,17,0.05)]" : "rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm"}`}>
                    <div className="text-xs uppercase text-slate-500">Pendientes</div>
                    <div className="mt-1 text-2xl font-black text-slate-950">{fmt(item.needsPlayersEntries)}</div>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <CapacityMeter
                    label="Cupo general"
                    taken={item.totalEntries}
                    cap={item.totalCap}
                    remaining={item.totalRemaining}
                    tone="cyan"
                    visualVariant={visualVariant}
                  />
                  <div className="grid grid-cols-1 gap-3 text-sm text-slate-700 md:grid-cols-2 xl:grid-cols-3">
                    {item.teamCap ? (
                      <CapacityMeter
                        label="Equipos"
                        taken={item.teamEntries}
                        cap={item.teamCap}
                        remaining={item.teamRemaining}
                        tone="violet"
                        visualVariant={visualVariant}
                      />
                    ) : null}
                    {item.soloCap ? (
                      <CapacityMeter
                        label="Jugadores individuales"
                        taken={item.soloEntries}
                        cap={item.soloCap}
                        remaining={item.soloRemaining}
                        tone="emerald"
                        visualVariant={visualVariant}
                      />
                    ) : null}
                    {item.freeAgentCap ? (
                      <CapacityMeter
                        label="Jugadores sin equipo"
                        taken={item.freeAgentEntries}
                        cap={item.freeAgentCap}
                        remaining={item.freeAgentRemaining}
                        tone="amber"
                        visualVariant={visualVariant}
                      />
                    ) : null}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-slate-700 md:grid-cols-2">
                  <div className={`${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-white/90 p-3 shadow-[0_10px_24px_rgba(17,17,17,0.05)]" : "rounded-xl border border-slate-200/80 bg-white p-3 shadow-sm"}`}>
                    <div>Tamaño esperado por equipo: <strong>{item.control.teamSize}</strong></div>
                    <div>Espacios de roster ya ocupados: <strong>{fmt(item.teamSpotsFilled)}</strong></div>
                    <div>Lista de espera activa: <strong>{item.control.waitlistEnabled ? "Sí" : "No"}</strong></div>
                  </div>
                </div>
              </StaticPanel>
            ))}
          </div>
          {!filteredByGame.length ? <p className="mt-4 text-sm text-slate-400">No encontramos ese juego en el panel.</p> : null}
        </StaticPanel>
          </>
        ) : null}

        {workspaceTab === "brackets" ? (
          <GamergyBracketManager
            apiPath={bracketApiPath}
            tournamentFilter={selectedGameMeta?.game || selectedGameMeta?.label || ""}
            enableExtendedModes={enableExtendedBracketModes}
          />
        ) : null}

        {workspaceTab === "registrations" ? (
        <>
        <StaticPanel className="p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="text-xl font-bold">Exportar registros</h2>
              <p className="mt-1 text-sm text-slate-600">
                Baja un CSV del panel actual para compartirlo con el resto del staff sin depender de Tournament Ops.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="text-sm text-slate-600">
                Alcance: <strong>{selectedGameMeta?.label || heroBranding?.title || "Todos los juegos"}</strong>
              </div>
              <ActionToneButton
                onClick={downloadCsvExport}
                disabled={actionBusy === "download:csv"}
                className="border-[#0f4cc9]/25 bg-[#edf4ff] text-[#0f4cc9] hover:bg-[#dfeaff] disabled:opacity-60"
              >
                {actionBusy === "download:csv" ? "Exportando..." : "Exportar CSV"}
              </ActionToneButton>
            </div>
          </div>
        </StaticPanel>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.12fr)_minmax(0,1.28fr)_minmax(0,0.92fr)]">
          <section className={`${isHandshakeVariant ? PREL_OPS_PANEL_CLASS : PREL_ADMIN_PANEL_CLASS} p-4 md:p-5`}>
            <h2 className="text-xl font-bold">Equipos</h2>
            <p className="mt-1 text-sm text-slate-600">Aquí puedes confirmar equipos, completar su roster y marcar el check-in por juego.</p>
            <div className="mt-4 space-y-3 overflow-auto pr-1 max-h-[38rem] xl:max-h-[calc(100vh-18rem)]">
              {filteredTeams.map((row) => (
                <article key={row.id} className={`${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-[linear-gradient(180deg,rgba(248,244,255,0.94),rgba(255,246,248,0.9))] p-4 shadow-[0_12px_32px_rgba(17,17,17,0.06)]" : "rounded-xl border border-slate-200/80 bg-[#f8fbff] p-3 shadow-sm"}`}>
                  {(() => {
                    const assignedPlayers = assignedFreeAgentsByTeamId[row.id] || [];
                    const teamMembers = buildTeamMemberList(row, assignedPlayers);
                    const displayStatus = getEntryDisplayStatus(row);
                    return (
                      <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="font-semibold">{row.team_name || "Equipo sin nombre"}</div>
                      <div className="text-xs text-slate-500">{row.control.label} · Capitán: {row.captain_name || `${row.first_name} ${row.last_name}`}</div>
                    </div>
                    <QueuePill label={displayStatus.label} status={displayStatus.status} visualVariant={visualVariant} />
                  </div>
                  <div className="mt-2 space-y-1 text-sm text-slate-700">
                    <div>Email: {row.email || "N/A"}</div>
                    <div>Discord username: {discordHandleLabel(row.captain_discord, row.discord, row.primary_handle)}</div>
                    <div>Jugadores principales: {row.rosterCoreCount}/{row.teamSizeTarget}</div>
                    <div>Suplentes: {row.rosterSubCount}</div>
                    <div>Faltan por completar: {row.rosterNeededCount}</div>
                    <div>Check-in: {row.checkinStatus || "pendiente"}</div>
                    <div>Recibido: {fmtDate(row.created_at)}</div>
                  </div>
                  <div className={`mt-3 text-sm text-slate-700 ${isHandshakeVariant ? "rounded-[22px] border border-black/10 bg-white/88 p-3" : "rounded-xl border border-slate-200 bg-white/80 p-3"}`}>
                    <div className="font-semibold text-slate-900">Roster visible</div>
                    <div className="mt-2 space-y-2">
                      {teamMembers.map((member, index) => (
                        <div key={`${member.label}:${index}`} className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white/85 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="font-medium text-slate-900">{member.label}</div>
                            <div className="text-xs text-slate-500">{member.role}</div>
                          </div>
                          {member.entryId ? (
                            <ActionToneButton
                              onClick={() => unassignFreeAgentFromTeam(assignedPlayers.find((player) => player.id === member.entryId) || { id: member.entryId })}
                              disabled={!!actionBusy}
                              visualVariant={visualVariant}
                              tone="waitlist"
                              className="sm:w-auto"
                            >
                              {actionBusy === `unassign:${member.entryId}` ? "Quitando..." : "Quitar del equipo"}
                            </ActionToneButton>
                          ) : null}
                        </div>
                      ))}
                    </div>
                    {!teamMembers.length ? (
                      <div className="mt-1 text-xs text-slate-500">Todavía no hay roster visible para este equipo.</div>
                    ) : null}
                    {parseDisplayList(row.roster_sub).length ? (
                      <div className="mt-3 border-t border-slate-200 pt-3">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Suplentes listados</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {parseDisplayList(row.roster_sub).map((member, index) => (
                            <span key={`${member}:${index}`} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                              {member}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                    <ActionToneButton onClick={() => performAction("promote_entry", { entryId: row.id, targetState: "confirmed" }, `confirm:${row.id}`)} disabled={!!actionBusy} visualVariant={visualVariant} tone="confirm">Confirmar</ActionToneButton>
                    <ActionToneButton onClick={() => performAction("promote_entry", { entryId: row.id, targetState: "waitlist" }, `wait:${row.id}`)} disabled={!!actionBusy} visualVariant={visualVariant} tone="waitlist">Lista de espera</ActionToneButton>
                    <ActionToneButton onClick={() => performAction("update_roster", { entryId: row.id, teamSizeTarget: row.teamSizeTarget, rosterCoreCount: row.rosterCoreCount + 1, rosterSubCount: row.rosterSubCount }, `roster:${row.id}`)} disabled={!!actionBusy} visualVariant={visualVariant} tone="roster">+1 jugador</ActionToneButton>
                    <ActionToneButton onClick={() => performAction("update_checkin", { entryId: row.id, checkinStatus: row.checkinStatus === "checked_in" ? "pending" : "checked_in", checkedInGame: row.game }, `checkin:${row.id}`)} disabled={!!actionBusy} visualVariant={visualVariant} tone="checkin">{row.checkinStatus === "checked_in" ? "Deshacer check-in" : "Check-in"}</ActionToneButton>
                    <ActionToneButton onClick={() => deleteRegistration({ ...row, entryType: "team" })} disabled={!!actionBusy} visualVariant={visualVariant} tone="danger">Borrar registro</ActionToneButton>
                  </div>
                      </>
                    );
                  })()}
                </article>
              ))}
              {!filteredTeams.length ? <p className="text-sm text-slate-400">No hay equipos con esos filtros.</p> : null}
            </div>
          </section>

          <section className={`${isHandshakeVariant ? PREL_OPS_PANEL_CLASS : PREL_ADMIN_PANEL_CLASS} p-4 md:p-5`}>
            <h2 className="text-xl font-bold">Jugadores sin equipo</h2>
            <p className="mt-1 text-sm text-slate-600">Aquí aparecen los jugadores que todavía no tienen equipo confirmado y necesitan acomodo.</p>
            {enableFreeAgentTeamBuilder ? (
              <div className="mt-4 space-y-3">
                <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-sm">
                  <div className="flex flex-col gap-3">
                    <div className="min-w-0">
                      <div className="text-base font-semibold leading-tight text-slate-900">Crear equipo nuevo desde free agents</div>
                      <div className="mt-1 text-xs leading-5 text-slate-500">
                        Selecciona jugadores del mismo juego, nombra el roster y créalo para seguir asignando o completar cupos.
                      </div>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                      <input
                        value={teamDrafts[selectedFreeAgentGame || gameFilter] || ""}
                        onChange={(e) => updateTeamDraft(selectedFreeAgentGame || gameFilter, e.target.value)}
                        placeholder="Nombre del equipo"
                        className={`w-full min-w-0 ${inputClass}`}
                      />
                      <ActionToneButton
                        onClick={createTeamFromSelectedFreeAgents}
                        disabled={!selectedFreeAgentRows.length || !selectedFreeAgentGame || !!actionBusy}
                        visualVariant={visualVariant}
                        className={isHandshakeVariant ? "w-full border-violet-200 bg-[linear-gradient(135deg,#ede9fe,#ffe4e6)] text-center text-[#6d28d9] hover:opacity-90 disabled:opacity-60 sm:w-auto sm:min-w-[11rem]" : "w-full border-[#0f4cc9]/25 bg-[#edf4ff] text-center text-[#0f4cc9] hover:bg-[#dfeaff] disabled:opacity-60 sm:w-auto sm:min-w-[11rem]"}
                      >
                        Crear equipo ({selectedFreeAgentRows.length})
                      </ActionToneButton>
                    </div>
                  </div>
                  {!selectedFreeAgentRows.length ? (
                    <div className="mt-2 text-xs text-slate-500">No hay free agents seleccionados todavía.</div>
                  ) : (
                    <div className="mt-2 text-xs text-slate-600">
                      Seleccionados: <strong>{selectedFreeAgentRows.map((row) => row.primary_handle || `${row.first_name} ${row.last_name}`).join(", ")}</strong>
                    </div>
                  )}
                </div>

                <div className={`${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-[linear-gradient(135deg,rgba(237,233,254,0.92),rgba(255,255,255,0.98),rgba(255,228,230,0.86))] p-4 shadow-[0_12px_32px_rgba(17,17,17,0.06)]" : "rounded-2xl border border-slate-200/80 bg-[linear-gradient(135deg,rgba(15,76,201,0.05),rgba(255,255,255,0.98),rgba(14,165,233,0.07))] p-4 shadow-sm"}`}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="text-base font-semibold leading-tight text-slate-900">Crear equipo manual</div>
                      <div className="mt-1 max-w-xl text-xs leading-5 text-slate-500">
                        Úsalo para abrir un roster vacío o levantar un record manual y luego irle asignando jugadores específicos.
                      </div>
                    </div>
                    <div className="self-start rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-800">
                      Registro manual
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Juego
                      <select
                        value={manualTeamDraft.game || (gameFilter !== "all" ? gameFilter : "")}
                        onChange={(e) => updateManualTeamDraft("game", e.target.value)}
                        className={`mt-2 w-full ${inputClass}`}
                        disabled={lockGameSelection}
                      >
                        <option value="">Escoge el juego...</option>
                        {byGame.map((item) => (
                          <option key={item.game} value={item.game}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Nombre del equipo
                      <input
                        value={manualTeamDraft.teamName}
                        onChange={(e) => updateManualTeamDraft("teamName", e.target.value)}
                        placeholder="Ej. Team Boricua"
                        className={`mt-2 w-full ${inputClass}`}
                      />
                    </label>
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Capitán o contacto
                      <input
                        value={manualTeamDraft.captainName}
                        onChange={(e) => updateManualTeamDraft("captainName", e.target.value)}
                        placeholder="Nombre principal"
                        className={`mt-2 w-full ${inputClass}`}
                      />
                    </label>
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Discord
                      <input
                        value={manualTeamDraft.captainDiscord}
                        onChange={(e) => updateManualTeamDraft("captainDiscord", e.target.value)}
                        placeholder="@usuario o invite"
                        className={`mt-2 w-full ${inputClass}`}
                      />
                    </label>
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Email
                      <input
                        value={manualTeamDraft.email}
                        onChange={(e) => updateManualTeamDraft("email", e.target.value)}
                        placeholder="contacto@equipo.com"
                        className={`mt-2 w-full ${inputClass}`}
                      />
                    </label>
                    <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                      Teléfono
                      <input
                        value={manualTeamDraft.phone}
                        onChange={(e) => updateManualTeamDraft("phone", e.target.value)}
                        placeholder="787-000-0000"
                        className={`mt-2 w-full ${inputClass}`}
                      />
                    </label>
                  </div>
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-slate-500">
                      Crea el roster primero y luego asígnale `free agents` o completa sus datos después.
                    </div>
                    <ActionToneButton
                      onClick={createManualTeamEntry}
                      disabled={!!actionBusy}
                      visualVariant={visualVariant}
                      className={isHandshakeVariant ? "w-full border-violet-200 bg-[linear-gradient(135deg,#ede9fe,#ffe4e6)] text-[#6d28d9] hover:opacity-90 disabled:opacity-60 sm:w-auto" : "w-full border-[#0f4cc9]/25 bg-[#edf4ff] text-[#0f4cc9] hover:bg-[#dfeaff] disabled:opacity-60 sm:w-auto"}
                    >
                      {actionBusy.startsWith("manual-team:") ? "Creando equipo..." : "Crear equipo manual"}
                    </ActionToneButton>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="mt-4 space-y-3 overflow-auto pr-1 max-h-[38rem] xl:max-h-[calc(100vh-18rem)]">
              {filteredFreeAgents.map((row) => (
                <article key={row.id} className={`${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-[linear-gradient(180deg,rgba(248,244,255,0.94),rgba(255,246,248,0.9))] p-4 shadow-[0_12px_32px_rgba(17,17,17,0.06)]" : "rounded-xl border border-slate-200/80 bg-[#f8fbff] p-3 shadow-sm"}`}>
                  {(() => {
                    const assignableTeams = (assignableTeamsByGame[row.game] || [])
                      .filter((team) => team.id !== row.assignedTeamEntryId)
                      .sort((a, b) => {
                        const needDelta = (b.rosterNeededCount || 0) - (a.rosterNeededCount || 0);
                        if (needDelta !== 0) return needDelta;
                        const aLabel = a.team_name || a.captain_name || "";
                        const bLabel = b.team_name || b.captain_name || "";
                        return aLabel.localeCompare(bLabel);
                      });
                    const selectedTeamId = assignmentDrafts[row.id] || "";
                    const busyKey = selectedTeamId ? `assign:${row.id}:${selectedTeamId}` : "";
                    const displayStatus = getEntryDisplayStatus(row);
                    return (
                      <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex items-start gap-3">
                      {enableFreeAgentTeamBuilder ? (
                        <input
                          type="checkbox"
                          checked={selectedFreeAgentIds.includes(row.id)}
                          onChange={() => toggleFreeAgentSelection(row.id)}
                          className="mt-1"
                        />
                      ) : null}
                      <div>
                      <div className="font-semibold">{row.first_name} {row.last_name}</div>
                      <div className="text-xs text-slate-500">{row.control.label} · {row.primary_handle || row.discord || "Sin handle"}</div>
                      </div>
                    </div>
                    <QueuePill label={displayStatus.label} status={displayStatus.status} visualVariant={visualVariant} />
                  </div>
                  <div className="mt-2 space-y-1 text-sm text-slate-700">
                    <div>Email: {row.email || "N/A"}</div>
                    <div>Discord username: {discordHandleLabel(row.discord, row.primary_handle)}</div>
                    <div>Teléfono: {row.phone || "N/A"}</div>
                    <div>Espacios que faltan para completar roster: {row.rosterNeededCount}</div>
                    <div>Check-in: {row.checkinStatus || "pendiente"}</div>
                    <div>Recibido: {fmtDate(row.created_at)}</div>
                  </div>
                  <div className={`mt-3 ${isHandshakeVariant ? "rounded-[22px] border border-black/10 bg-white/88 p-3" : "rounded-xl border border-slate-200 bg-white/85 p-3"}`}>
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Asignación manual</div>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                      <select
                        value={selectedTeamId}
                        onChange={(e) => updateAssignmentDraft(row.id, e.target.value)}
                        className={`w-full sm:flex-1 ${inputClass}`}
                        disabled={!assignableTeams.length || !!actionBusy}
                      >
                        <option value="">Escoge el equipo destino...</option>
                        {assignableTeams.map((team) => (
                          <option key={team.id} value={team.id}>
                            {(team.team_name || team.captain_name || "Equipo sin nombre")}
                            {team.rosterNeededCount > 0 ? ` · faltan ${team.rosterNeededCount}` : " · roster completo"}
                          </option>
                        ))}
                      </select>
                      <ActionToneButton
                        onClick={() => assignFreeAgentToSelectedTeam(row)}
                        disabled={!selectedTeamId || !assignableTeams.length || !!actionBusy}
                        visualVariant={visualVariant}
                        tone="assign"
                      >
                        {actionBusy === busyKey ? "Asignando..." : "Asignar a equipo"}
                      </ActionToneButton>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      {assignableTeams.length
                        ? `${assignableTeams.length} equipos disponibles para ${row.control.label}.`
                        : "No hay equipos disponibles en este juego para asignarle este jugador."}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                    <ActionToneButton onClick={() => performAction("promote_entry", { entryId: row.id, targetState: "waitlist" }, `wait:${row.id}`)} disabled={!!actionBusy} visualVariant={visualVariant} tone="waitlist">Lista de espera</ActionToneButton>
                    <ActionToneButton onClick={() => performAction("update_checkin", { entryId: row.id, checkinStatus: row.checkinStatus === "checked_in" ? "pending" : "checked_in", checkedInGame: row.game }, `checkin:${row.id}`)} disabled={!!actionBusy} visualVariant={visualVariant} tone="checkin">{row.checkinStatus === "checked_in" ? "Deshacer check-in" : "Check-in"}</ActionToneButton>
                    <ActionToneButton onClick={() => deleteRegistration({ ...row, entryType: "free_agent" })} disabled={!!actionBusy} visualVariant={visualVariant} tone="danger">Borrar registro</ActionToneButton>
                  </div>
                      </>
                    );
                  })()}
                </article>
              ))}
              {!filteredFreeAgents.length ? <p className="text-sm text-slate-400">No hay jugadores sin equipo con esos filtros.</p> : null}
            </div>
          </section>

          <section className={`${isHandshakeVariant ? PREL_OPS_PANEL_CLASS : PREL_ADMIN_PANEL_CLASS} p-4 md:p-5`}>
            <h2 className="text-xl font-bold">Jugadores individuales</h2>
            <p className="mt-1 text-sm text-slate-600">Títulos 1v1 o registros individuales que no dependen de roster.</p>
            <div className="mt-4 space-y-3 overflow-auto pr-1 max-h-[38rem] xl:max-h-[calc(100vh-18rem)]">
              {filteredSoloPlayers.map((row) => (
                <article key={row.id} className={`${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-[linear-gradient(180deg,rgba(248,244,255,0.94),rgba(255,246,248,0.9))] p-4 shadow-[0_12px_32px_rgba(17,17,17,0.06)]" : "rounded-xl border border-slate-200/80 bg-[#f8fbff] p-3 shadow-sm"}`}>
                  {(() => {
                    const displayStatus = getEntryDisplayStatus(row);
                    return (
                      <>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="font-semibold">{row.first_name} {row.last_name}</div>
                      <div className="text-xs text-slate-500">{row.control.label} · {row.primary_handle || row.discord || "Sin handle"}</div>
                    </div>
                    <QueuePill label={displayStatus.label} status={displayStatus.status} visualVariant={visualVariant} />
                  </div>
                  <div className="mt-2 space-y-1 text-sm text-slate-700">
                    <div>Email: {row.email || "N/A"}</div>
                    <div>Discord username: {discordHandleLabel(row.discord, row.primary_handle)}</div>
                    <div>Teléfono: {row.phone || "N/A"}</div>
                    <div>Check-in: {row.checkinStatus || "pendiente"}</div>
                    <div>Recibido: {fmtDate(row.created_at)}</div>
                  </div>
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                    <ActionToneButton onClick={() => performAction("promote_entry", { entryId: row.id, targetState: "confirmed" }, `confirm:${row.id}`)} disabled={!!actionBusy} visualVariant={visualVariant} tone="confirm">Confirmar</ActionToneButton>
                    <ActionToneButton onClick={() => performAction("promote_entry", { entryId: row.id, targetState: "waitlist" }, `wait:${row.id}`)} disabled={!!actionBusy} visualVariant={visualVariant} tone="waitlist">Lista de espera</ActionToneButton>
                    <ActionToneButton onClick={() => performAction("promote_entry", { entryId: row.id, targetState: "closed" }, `close:${row.id}`)} disabled={!!actionBusy} visualVariant={visualVariant} tone="close">Cerrar</ActionToneButton>
                    <ActionToneButton onClick={() => performAction("update_checkin", { entryId: row.id, checkinStatus: row.checkinStatus === "checked_in" ? "pending" : "checked_in", checkedInGame: row.game }, `checkin:${row.id}`)} disabled={!!actionBusy} visualVariant={visualVariant} tone="checkin">{row.checkinStatus === "checked_in" ? "Deshacer check-in" : "Check-in"}</ActionToneButton>
                    <ActionToneButton onClick={() => deleteRegistration({ ...row, entryType: "solo" })} disabled={!!actionBusy} visualVariant={visualVariant} tone="danger">Borrar registro</ActionToneButton>
                  </div>
                      </>
                    );
                  })()}
                </article>
              ))}
              {!filteredSoloPlayers.length ? <p className="text-sm text-slate-400">No hay jugadores individuales con esos filtros.</p> : null}
            </div>
          </section>
        </div>

        <section className={`${isHandshakeVariant ? PREL_OPS_PANEL_CLASS : PREL_ADMIN_PANEL_CLASS} p-4 md:p-5`}>
          <h2 className="text-xl font-bold">Jugadores ya asignados</h2>
          <p className="mt-1 text-sm text-slate-600">Referencia rápida de jugadores que ya fueron acomodados manualmente en un equipo.</p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filteredAssignedFreeAgents.map((row) => (
              <article key={row.id} className={`${isHandshakeVariant ? "rounded-[24px] border border-black/10 bg-[linear-gradient(180deg,rgba(248,244,255,0.94),rgba(255,246,248,0.9))] p-4 shadow-[0_12px_32px_rgba(17,17,17,0.06)]" : "rounded-xl border border-slate-200/80 bg-[#f8fbff] p-3 shadow-sm"}`}>
                <div className="font-semibold">{row.first_name} {row.last_name}</div>
                <div className="mt-1 text-xs text-slate-500">{row.control.label}</div>
                <div className="mt-2 text-sm text-slate-700">Discord username: {discordHandleLabel(row.discord, row.primary_handle)}</div>
                <div className="mt-2 text-sm text-slate-700">Asignado a: {row.assignedTeamName || "N/A"}</div>
                <div className="text-sm text-slate-700">Check-in: {row.checkinStatus || "pendiente"}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <ActionToneButton onClick={() => unassignFreeAgentFromTeam(row)} disabled={!!actionBusy} visualVariant={visualVariant} tone="waitlist">
                    {actionBusy === `unassign:${row.id}` ? "Quitando..." : "Mover a free agent"}
                  </ActionToneButton>
                  <ActionToneButton onClick={() => deleteRegistration({ ...row, entryType: "free_agent" })} disabled={!!actionBusy} visualVariant={visualVariant} tone="danger">Borrar registro</ActionToneButton>
                </div>
              </article>
            ))}
            {!filteredAssignedFreeAgents.length ? <p className="text-sm text-slate-400">Todavía no hay jugadores asignados a equipos en este filtro.</p> : null}
          </div>
        </section>

        <section className={`${isHandshakeVariant ? PREL_OPS_PANEL_CLASS : PREL_ADMIN_PANEL_CLASS} p-4 md:p-5`}>
          <h2 className="text-xl font-bold">Últimos registros</h2>
          <p className="mt-1 text-sm text-slate-600">Vista rápida para apoyo al staff, seguimiento manual y resolución de dudas.</p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[920px] text-sm">
              <thead className="text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Juego</th>
                  <th className="px-3 py-2 text-left">Nombre</th>
                  <th className="px-3 py-2 text-left">Modo</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-left">Estado</th>
                  <th className="px-3 py-2 text-left">Discord username</th>
                  <th className="px-3 py-2 text-left">Contacto</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecent.map((row) => (
                  <tr key={row.id} className="border-b border-slate-200/80">
                    <td className="px-3 py-3 text-slate-700">{fmtDate(row.created_at)}</td>
                    <td className="px-3 py-3 text-slate-700">{row.control.label}</td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{row.first_name} {row.last_name}</div>
                      <div className="text-xs text-slate-500">{row.team_name || row.primary_handle || "Sin equipo / handle"}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{row.mode}</td>
                    <td className="px-3 py-3 text-slate-700">{row.entryType}</td>
                    <td className="px-3 py-3"><QueuePill label={getEntryDisplayStatus(row).label} status={getEntryDisplayStatus(row).status} visualVariant={visualVariant} /></td>
                    <td className="px-3 py-3 text-slate-700">{discordHandleLabel(row.captain_discord, row.discord, row.primary_handle)}</td>
                    <td className="px-3 py-3 text-slate-700">{row.email || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filteredRecent.length && !loading ? <p className="text-sm text-slate-400 mt-4">Todavía no hay registros cargados para este filtro.</p> : null}
          </div>
        </section>
        </>
        ) : null}

        <TournamentOpsFooter visualVariant={visualVariant} />
      </div>
    </div>
  );
}
